import { getModeByName, ModeCategory } from '@commutifi/modes'
import { modeFromGoogle } from '@commutifi/modes/google'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCaretRight } from '@fortawesome/free-solid-svg-icons'
import cx from 'classnames'
import { get, isEqual, omit, pick, uniqWith } from 'lodash'
import moment from 'moment-timezone'
import React, { Children, cloneElement, isValidElement } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Button as AppButton,
  Empty,
  GenericError,
  HorizontalList,
  SelectableCardGrid,
  Skeleton,
  TransportTag
} from '@commutifi-fe/ui'
import { fetchTransitOptions } from 'api/thirdParty/google'
import { DEFAULT_OFFICE_ARRIVAL_TIME_HHMM, sortingOrdersEnum } from 'constants/global'
import { LEG_TYPES } from '@commutifi-fe/constants'
import { FormattedMessage } from 'locales/index'
import { FormItemInjectedProps } from 'shared/interfaces'
import { CommuteTransport, TransitOption as TransitOptionInterface } from 'shared/interfaces/Commute'
import { Location } from 'shared/interfaces/Location'
import { isLastItem, sortByKey } from 'utils/helpers'
import styles from './styles.module.scss'
import { ModeIcon } from '@commutifi-fe/commutifi-specific/Presentation/ModeIcon'

const clsPrefix = 'transit-options'

export interface GoogleTransitDirectionsProps extends FormItemInjectedProps<TransitOptionInterface> {
  enabled?: boolean
  // If you need to tell how to get/format the value
  // from the onChange event
  getValueFromEvent?: (route: TransitOptionInterface) => any
  // Controlled form value. Not used since the results
  // from google directions api could be different so we want
  // the user to select the value again
  transitValue?: TransitOptionInterface
  startLocation: Location
  startAddress?: string
  endLocation: Location
  endAddress?: string
  arrivalTime?: string
  departureTime?: string
  timezone?: string | null
  children?: React.ReactNode
}

interface TransitOptionProps {
  route: TransitOptionInterface
}

// Filter the google steps that we should not consider in our routes.
// This is to filter the walking steps where you walk from the metro to the bus
// right outside the metro station
const isConsiderableDistance = (distance: number) => distance > 100

/**
 * Set time of arrival or departure to wednesday. That way we make sure
 * it is not an holiday and google will return more accurate results
 * @param momentTime - Moment object representing the time you want to set wednesday to
 */
const setMomentToWednesday = (timeString: string, timeFormat = 'Hmm', timezone?: string | null) => {
  const momentWithoutTimezone = moment(timeString, timeFormat).add(1, 'week').day('wednesday')
  return timezone ? momentWithoutTimezone.tz(timezone, true) : momentWithoutTimezone
}

const formatGoogleRoutes = (directions: google.maps.DirectionsResult | null): TransitOptionInterface[] => {
  const formattedRoutes = (directions?.routes || []).map((route) => {
    const firstLeg = route.legs[0]
    return {
      formattedDuration: firstLeg.duration?.text,
      durationInSec: firstLeg.duration?.value,
      startAddress: firstLeg.start_address,
      startLocation: {
        lat: firstLeg.start_location.lat(),
        lng: firstLeg.start_location.lng()
      },
      endAddress: firstLeg.end_address,
      endLocation: {
        lat: firstLeg.end_location.lat(),
        lng: firstLeg.end_location.lng()
      },
      legs: firstLeg.steps.map((step, index) => {
        const transitLine = step.transit?.line || {
          short_name: undefined,
          name: undefined,
          color: undefined,
          text_color: undefined
        }

        const modeName = modeFromGoogle(step.travel_mode, step.transit?.line.vehicle.type)
        const mode = modeName ? getModeByName(modeName) : { name: undefined, category: undefined }
        const transport: CommuteTransport = {
          mode: mode.name,
          category: mode.category
        }
        return {
          category: transport.category,
          mode: transport.mode,
          type: LEG_TYPES.leg,
          startAddress: index === 0 ? firstLeg.start_address : '',
          startLocation: {
            lat: step.start_location.lat(),
            lng: step.start_location.lng()
          },
          distance: step.distance?.value,
          endAddress: isLastItem(index, firstLeg.steps.length) ? firstLeg.end_address : '',
          endLocation: {
            lat: step.end_location.lat(),
            lng: step.end_location.lng()
          },
          ...(Object.keys(transitLine).length && {
            name: transitLine.short_name || transitLine.name,
            displayColor: transitLine.color,
            textColor: transitLine.text_color,
            icon: get(transitLine, 'vehicle.icon')
          })
        }
      })
    }
  })

  // Filter google routes that have the same amount of details for our platform since
  // google proposes the same route with different arrival times and we only need to know
  // know the details of the route
  const nonDuplicatedRoutes = uniqWith(formattedRoutes, isEqual)
  return sortByKey(nonDuplicatedRoutes, 'durationInSec', sortingOrdersEnum.ASC)
}

function LoadingTransitOption() {
  return (
    <div className="u-margin-horizontal--medium">
      <Skeleton active loading title={false} paragraph={{ rows: 1, width: 350 }} />
    </div>
  )
}

function Loading() {
  return (
    <SelectableCardGrid block>
      <SelectableCardGrid.ClickableCard id="loading-1">
        <LoadingTransitOption />
      </SelectableCardGrid.ClickableCard>
      <SelectableCardGrid.ClickableCard id="loading-2">
        <LoadingTransitOption />
      </SelectableCardGrid.ClickableCard>
      <SelectableCardGrid.ClickableCard id="loading-3">
        <LoadingTransitOption />
      </SelectableCardGrid.ClickableCard>
    </SelectableCardGrid>
  )
}

// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents -- Legacy
const getGoogleErrorKey = (status?: unknown | google.maps.DirectionsStatus | 'MAX_ROUTE_LENGTH_EXCEEDED') => {
  if (typeof status !== 'string') {
    return
  }

  switch (status || '') {
    case 'MAX_ROUTE_LENGTH_EXCEEDED':
      return 'google.errors.code.maxRouteLength.description'
    case google.maps.DirectionsStatus.NOT_FOUND:
      return 'google.errors.code.maxRouteLength.description'
    case google.maps.DirectionsStatus.UNKNOWN_ERROR:
    default:
      return 'google.errors.code.unknown.description'
  }
}

export function TransitOption({ route }: TransitOptionProps) {
  return (
    <div className={styles[`${clsPrefix}-container`]}>
      <HorizontalList
        items={route.legs
          .filter((leg) => (leg.distance ? isConsiderableDistance(leg.distance) : true))
          .map((leg, index) => (
            <div key={index}>
              {leg.icon ? (
                <TransportTag
                  icon={<img src={leg.icon} alt="Transit line icon" height={24} />}
                  color={leg.displayColor}
                  text={leg.name}
                  textColor={leg.textColor}
                  className="u-margin-left--tiny"
                />
              ) : (
                <TransportTag icon={<ModeIcon mode={leg.category} />} className="u-margin-left--tiny" />
              )}
            </div>
          ))}
        separator={<FontAwesomeIcon className="u-color--neutral-500" icon={faCaretRight} />}
      />
      <div className={styles[`${clsPrefix}-list-item-add-on`]}>{route.formattedDuration}</div>
    </div>
  )
}

function GoogleTransitDirections({
  id,
  enabled = true,
  onChange,
  getValueFromEvent,
  startLocation,
  startAddress,
  endLocation,
  endAddress,
  arrivalTime = DEFAULT_OFFICE_ARRIVAL_TIME_HHMM,
  departureTime,
  timezone,
  transitValue,
  children
}: GoogleTransitDirectionsProps) {
  const { lat: startLat, lng: startLng } = startLocation || {}
  const { lat: endLat, lng: endLng } = endLocation || {}
  const directionsQueryParams = React.useMemo<google.maps.DirectionsRequest>(() => {
    // Construct moment object and parse from our time format defined in constants.
    // This will allow us to convert those time to a date which is needed in google query params
    const arrivalMoment = arrivalTime ? setMomentToWednesday(arrivalTime, 'Hmm', timezone) : null
    const departureMoment = departureTime ? setMomentToWednesday(departureTime, 'Hmm', timezone) : null
    return {
      origin: { lat: startLat, lng: startLng, ...(startAddress && { query: startAddress }) },
      destination: { lat: endLat, lng: endLng, ...(endAddress && { query: endAddress }) },
      transitOptions: {
        ...(departureMoment && departureMoment.isValid() ? { departureTime: departureMoment.toDate() } : {}),
        ...(arrivalMoment && arrivalMoment.isValid() ? { arrivalTime: arrivalMoment.toDate() } : {})
      },
      travelMode: google.maps.TravelMode.TRANSIT
    }
  }, [startLat, startLng, endLat, endLng, arrivalTime, departureTime, timezone, startAddress, endAddress])

  // Fetch and format google directions each time the query parameters change
  const {
    isLoading: loading,
    data: transitOptions,
    isFetched: loaded,
    error,
    refetch: retry
  } = useQuery({
    queryKey: ['transit-options', directionsQueryParams],
    queryFn: async () => fetchTransitOptions(directionsQueryParams),
    select: formatGoogleRoutes,
    enabled: Boolean(enabled && (startLocation || startAddress || endLocation || endAddress))
  })

  const handleOnChange = (route: TransitOptionInterface) => {
    // Make sure we have at least a start and end address on the first and last leg respectively
    const firstLeg = route.legs[0]
    if (firstLeg && !firstLeg.startAddress) {
      route.legs[0] = { ...firstLeg, startAddress: startAddress || '' }
    }
    const lastLeg = route.legs[route.legs.length - 1]
    if (lastLeg && !lastLeg.endAddress) {
      route.legs[route.legs.length - 1] = { ...lastLeg, endAddress: endAddress || '' }
    }

    if (onChange) {
      onChange(getValueFromEvent ? getValueFromEvent(route) : route)
    }
  }

  const handleSelectMyRouteNotListed = () => {
    // My route is not listed -> we don't know the details but we know the
    // transit mode category which is public_transit without knowing the mode name (ex. bus)
    const tempRoute = {
      startAddress: startAddress || '',
      endAddress: endAddress || '',
      startLocation,
      endLocation,
      legs: [
        {
          startAddress: startAddress || '',
          endAddress: endAddress || '',
          startLocation,
          endLocation,
          category: ModeCategory.PublicTransit,
          type: LEG_TYPES.leg
        }
      ]
    }
    handleOnChange(tempRoute)
  }

  const handleRetry = () => {
    retry()
  }

  const renderTransitOptions = () => (
    <SelectableCardGrid id={id} block>
      {transitOptions?.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<FormattedMessage id="commuteRoute.modes.transit.noResults" />}
        />
      ) : (
        transitOptions?.map((transitOption: TransitOptionInterface, index: number) => (
          <SelectableCardGrid.ClickableCard
            key={`transit-option-${index}`}
            id={`transit-option-${index}`}
            title={<TransitOption route={transitOption} />}
            onClick={() => handleOnChange(transitOption)}
            initialSelected={transitValue ? isEqual(transitValue, transitOption) : undefined}
          />
        ))
      )}
      {Children.map(children, (child, index) => {
        if (!child || !isValidElement(child)) {
          return null
        }
        const id = child.props.id || `transit-other-option-${index}`
        return (
          <SelectableCardGrid.ClickableCard id={id} onClick={handleSelectMyRouteNotListed}>
            {cloneElement(child, child.props)}
          </SelectableCardGrid.ClickableCard>
        )
      })}
    </SelectableCardGrid>
  )

  return error ? (
    <GenericError
      title={<FormattedMessage id="onboarding.transitSelector.fetching.error" />}
      description={getGoogleErrorKey(error) ? <FormattedMessage id={getGoogleErrorKey(error)} /> : ''}
      actions={
        <AppButton
          onClick={handleRetry}
          loading={loading}
          className={cx(getGoogleErrorKey(error) && 'u-margin-top--medium')}
        >
          <span>
            <FormattedMessage id="global.retry" />
          </span>
        </AppButton>
      }
    />
  ) : loading || !loaded ? (
    <Loading />
  ) : (
    renderTransitOptions()
  )
}

function NotListedOption() {
  return (
    <div className="u-flex-align-center u-flex-justify-center u-font-weight-semi">
      <FormattedMessage id="onboarding.transitSelector.options.notListed" />
    </div>
  )
}

GoogleTransitDirections.OtherOption = NotListedOption

/**
 * Returns a google transit route but wraps it in a commutifi formatted route
 * @param props - {@link GoogleTransitDirectionsProps}
 */
function CommutifiTransitRoute(props: GoogleTransitDirectionsProps) {
  return (
    <GoogleTransitDirections
      {...props}
      getValueFromEvent={(googleRoute) => ({
        ...pick(googleRoute, ['startAddress', 'startLocation', 'endAddress', 'endLocation']),
        legs: [
          {
            ...omit(googleRoute, 'legs'),
            category: ModeCategory.PublicTransit,
            type: LEG_TYPES.leg,
            transit: googleRoute
          }
        ]
      })}
    />
  )
}

GoogleTransitDirections.CommutifiTransitRoute = CommutifiTransitRoute
export default GoogleTransitDirections
